import {
  COOKIE_TOKEN_KEY,
  COOKIE_REFRESH_TOKEN_KEY,
  COOKIE_UPDATE_REFRESH_TOKEN_KEY,
  COOKIE_PATH,
  COOKIE_MAX_AGE_SECONDS,
} from '~/shared/const/cookie'
import type { CookieRef } from '#app'

interface CookieOptions {
  path?: string
  maxAge?: number | null // null - кука до закрытия браузера
  sameSite?: 'lax' | 'strict' | 'none'
  secure?: boolean
  httpOnly?: boolean
}

// На SSR useCookie отдаёт значение из заголовка запроса, поэтому записанный
// в этом же рендере токен (после refresh) без кеша не виден.
// nuxtApp на сервере свой для каждого запроса, в payload не попадает.
const useTokenCache = (): Record<string, string | null> => {
  const nuxtApp = useNuxtApp() as unknown as { _userTokenCache?: Record<string, string | null> }

  nuxtApp._userTokenCache ??= {}

  return nuxtApp._userTokenCache
}

export const useUserToken = () => {
  const cache = useTokenCache()

  // Каждый useCookie вешает на cookieStore слушатель change, который снимается
  // только при dispose scope. Геттеры зовутся из глобального middleware, вне
  // scope компонента, поэтому ref'ы кешируются на экземпляр nuxtApp.
  const nuxtApp = useNuxtApp() as unknown as {
    _userTokenRefs?: Record<string, CookieRef<string | null>>
  }
  nuxtApp._userTokenRefs ??= {}
  const refs = nuxtApp._userTokenRefs
  // Общие опции для всех кук
  const defaultOptions: CookieOptions = {
    path: COOKIE_PATH,
    maxAge: COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax',
    secure: !import.meta.dev,
  }

  // Универсальный метод для получения токена
  const getToken = (key: string = COOKIE_TOKEN_KEY): string | null => {
    if (key in cache) {
      return cache[key] ?? null
    }

    refs[key] ??= useCookie<string | null>(key)
    return refs[key].value ?? null
  }

  // Специализированные геттеры
  const getAccessToken = (): string | null => {
    return getToken(COOKIE_TOKEN_KEY)
  }

  const getRefreshToken = (): string | null => {
    return getToken(COOKIE_REFRESH_TOKEN_KEY)
  }

  const getUpdateRefreshToken = (): string | null => {
    return getToken(COOKIE_UPDATE_REFRESH_TOKEN_KEY)
  }

  // Универсальный метод для установки токена
  const setToken = (key: string, token: string, options?: CookieOptions): void => {
    const { maxAge, ...restOptions } = { ...defaultOptions, ...options }

    const cookie = useCookie(key, {
      ...restOptions,
      // для сессионной куки опцию нужно не передавать вовсе
      ...(maxAge === null || maxAge === undefined ? {} : { maxAge }),
    })
    cookie.value = token
    cache[key] = token
  }

  // Специализированные сеттеры
  const setAccessToken = (token: string, rememberMe: boolean = true): void => {
    setToken(COOKIE_TOKEN_KEY, token, {
      maxAge: rememberMe ? COOKIE_MAX_AGE_SECONDS : null,
    })
  }

  const setRefreshToken = (token: string, rememberMe: boolean = true): void => {
    setToken(COOKIE_REFRESH_TOKEN_KEY, token, {
      maxAge: rememberMe ? COOKIE_MAX_AGE_SECONDS : null,
    })
  }

  const setUpdateRefreshToken = (token: string, rememberMe: boolean = true): void => {
    setToken(COOKIE_UPDATE_REFRESH_TOKEN_KEY, token, {
      maxAge: rememberMe ? COOKIE_MAX_AGE_SECONDS : null,
    })
  }

  // Переносит одноразовый update_refresh_token в refresh_token
  const consumeUpdateRefreshToken = (token: string): void => {
    if (!token) {
      return
    }

    setRefreshToken(token)
    removeToken(COOKIE_UPDATE_REFRESH_TOKEN_KEY)
  }

  // Универсальный метод для удаления токена
  const removeToken = (key: string): void => {
    refs[key] ??= useCookie<string | null>(key)
    refs[key].value = null
    cache[key] = null
  }

  // Удаление всех токенов
  const removeAllTokens = (): void => {
    removeToken(COOKIE_TOKEN_KEY)
    removeToken(COOKIE_REFRESH_TOKEN_KEY)
    removeToken(COOKIE_UPDATE_REFRESH_TOKEN_KEY)
  }

  // Проверка наличия токенов
  const hasTokens = (): boolean => {
    return !!getAccessToken() || !!getRefreshToken()
  }

  // Получение всех токенов одним объектом
  const getAllTokens = (): {
    accessToken: string | null
    refreshToken: string | null
    updateRefreshToken: string | null
  } => {
    return {
      accessToken: getAccessToken(),
      refreshToken: getRefreshToken(),
      updateRefreshToken: getUpdateRefreshToken(),
    }
  }

  return {
    // Геттеры
    getAccessToken,
    getRefreshToken,
    getUpdateRefreshToken,
    getAllTokens,
    hasTokens,

    // Сеттеры
    setAccessToken,
    setRefreshToken,
    setUpdateRefreshToken,
    consumeUpdateRefreshToken,

    // Удаление
    removeToken,
    removeAllTokens,

    // Низкоуровневые методы (для особых случаев)
    _getToken: getToken,
    _setToken: setToken,
  }
}
